import {
  DeleteWaitlistEntry,
  GetWaitlistBookableForms,
  GetWaitlistNotifications,
  GetWaitlistSetting,
  GetWaitlistStats,
  PostWaitlistAcceptGroup,
  PostWaitlistCreate,
  PostWaitlistCreateRegular,
  PostWaitlistExport,
  PostWaitlistList,
  PostWaitlistNotifyNow,
  PutWaitlistSetting,
} from "@/server-types";
import type { AxiosError } from "axios";
import type {
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { del, get, post, put } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";

type QueryOpts<TData> = Omit<
  UseQueryOptions<TData, AxiosError, TData>,
  "queryKey" | "queryFn"
> & { displayNotification?: boolean };

type MutationOpts<TData, TVariables> = Omit<
  UseMutationOptions<TData, AxiosError, TVariables>,
  "mutationFn" | "onError"
> & {
  displayNotification?: boolean;
  onError?: (
    error: AxiosError,
    variables: TVariables,
    context: unknown,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
};

export const useGetSetting = (
  { locationId }: { locationId: number },
  options?: QueryOpts<GetWaitlistSetting["response"]>,
) => {
  const { enabled, ...rest } = options ?? {};
  return useCustomQuery({
    queryKey: ["waitlist", "setting", locationId],
    queryFn: () => get<GetWaitlistSetting>(`/waitlist/setting/${locationId}`),
    ...rest,
    enabled: (enabled ?? true) && locationId > 0,
  });
};

export const usePutSetting = (
  options?: MutationOpts<
    PutWaitlistSetting["response"],
    { locationId: number; body: PutWaitlistSetting["body"] }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: PutWaitlistSetting["body"];
    }) => put<PutWaitlistSetting>(`/waitlist/setting/${locationId}`, body),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: ["waitlist", "setting", variables.locationId],
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useGetList = (
  body: PostWaitlistList["body"],
  options?: QueryOpts<PostWaitlistList["response"]>,
) => {
  return useCustomQuery({
    queryKey: ["waitlist-list", body],
    queryFn: () => post<PostWaitlistList>("/waitlist/list", body),
    ...options,
  });
};

export const useGetNotifications = (
  { waitlistId }: { waitlistId: number },
  options?: QueryOpts<GetWaitlistNotifications["response"]>,
) => {
  const { enabled, ...rest } = options ?? {};
  return useCustomQuery({
    queryKey: ["waitlist", "notifications", waitlistId],
    queryFn: () =>
      get<GetWaitlistNotifications>(`/waitlist/${waitlistId}/notifications`),
    ...rest,
    enabled: (enabled ?? true) && waitlistId > 0,
  });
};

export const useGetStats = (
  query: GetWaitlistStats["query"] = {},
  options?: QueryOpts<GetWaitlistStats["response"]>,
) => {
  return useCustomQuery({
    queryKey: ["waitlist-stats", query],
    queryFn: () => get<GetWaitlistStats>("/waitlist/stats", query),
    ...options,
  });
};

export const useGetBookableForms = (
  options?: QueryOpts<GetWaitlistBookableForms["response"]>,
) => {
  return useCustomQuery({
    queryKey: ["waitlist", "bookable-forms"],
    queryFn: () => get<GetWaitlistBookableForms>("/waitlist/bookable-forms"),
    ...options,
  });
};

export const useDeleteEntry = (
  options?: MutationOpts<
    DeleteWaitlistEntry["response"],
    { waitlistId: number }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({ waitlistId }: { waitlistId: number }) =>
      del<DeleteWaitlistEntry>(`/waitlist/${waitlistId}`),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ["waitlist-list"] });
      queryClient.invalidateQueries({ queryKey: ["waitlist-stats"] });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const usePostNotifyNow = (
  options?: MutationOpts<
    PostWaitlistNotifyNow["response"],
    { waitlistId: number; body: PostWaitlistNotifyNow["body"] }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({
      waitlistId,
      body,
    }: {
      waitlistId: number;
      body: PostWaitlistNotifyNow["body"];
    }) =>
      post<PostWaitlistNotifyNow>(`/waitlist/${waitlistId}/notify-now`, body),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ["waitlist-list"] });
      queryClient.invalidateQueries({ queryKey: ["waitlist-stats"] });
      queryClient.invalidateQueries({
        queryKey: ["waitlist", "notifications", variables.waitlistId],
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const usePostAcceptGroup = (
  options?: MutationOpts<
    PostWaitlistAcceptGroup["response"],
    { waitlistId: number }
  >,
) => {
  return useCustomMutation({
    mutationFn: ({ waitlistId }: { waitlistId: number }) =>
      post<PostWaitlistAcceptGroup>(`/waitlist/${waitlistId}/accept-group`, {}),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ["waitlist-list"] });
      queryClient.invalidateQueries({ queryKey: ["waitlist-stats"] });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const usePostCreate = (
  options?: MutationOpts<
    PostWaitlistCreate["response"],
    PostWaitlistCreate["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PostWaitlistCreate["body"]) =>
      post<PostWaitlistCreate>("/waitlist", body),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ["waitlist-list"] });
      queryClient.invalidateQueries({ queryKey: ["waitlist-stats"] });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const usePostExport = (
  options?: MutationOpts<
    PostWaitlistExport["response"],
    PostWaitlistExport["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PostWaitlistExport["body"]) =>
      post<PostWaitlistExport>("/waitlist/export", body),
    ...options,
  });
};

export const usePostCreateRegular = (
  options?: MutationOpts<
    PostWaitlistCreateRegular["response"],
    PostWaitlistCreateRegular["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PostWaitlistCreateRegular["body"]) =>
      post<PostWaitlistCreateRegular>("/waitlist/regular", body),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ["waitlist-list"] });
      queryClient.invalidateQueries({ queryKey: ["waitlist-stats"] });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
